import React, { useEffect, useMemo, useRef, useState } from 'react';
import 'primeicons/primeicons.css';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { carteraApi } from './api';
import { money, money0, iniciales, PROD, prodCorto, stageStyle, etapaLabelDe, Pill } from './helpers';
// Derivación del estado de cuenta (vista + datos del PDF). Compartida con
// Conversaciones, que manda el MISMO PDF por WhatsApp.
import {
  fmtDate, hoyISO, periodoTitulo, cuotaTexto, filaEstatus,
  buildEdoCuentaView, buildEdoCuentaPdfData,
} from './edoCuenta';
import { MensajeModal, DATOS_PAGO } from './mensajes';
import { EstadoCuentaPDF } from './EstadoCuentaPDF';
import { EnviarEdoCuentaModal } from './EnviarEdoCuentaModal';

const TRI = 'linear-gradient(90deg,#204B7B,#32B8DF,#00BA6E)';

const FinCard = ({ f }) => (
  <div style={{ background: 'linear-gradient(160deg,#F8FBFD,#F1F6FA)', border: '1px solid #EAF0F5', borderRadius: 14, padding: '14px 15px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 9 }}>
      <span className={'pi ' + f.icon} style={{ color: '#32B8DF', fontSize: 13 }} />
      <span style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>{f.label}</span>
    </div>
    <div className="tnum" style={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>{f.value}</div>
    <div style={{ fontSize: 11, color: '#64748B', marginTop: 3 }}>{f.sub}</div>
  </div>
);

export const CarteraEstadosCuenta = ({ preselectId, onSelectContrato }) => {
  const [query, setQuery] = useState('');
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [selId, setSelId] = useState(preselectId || null);

  // La selección la manda la URL (preselectId): recarga y atrás/adelante la
  // reflejan. Solo actualiza si cambió, para no re-fetchear en cada render.
  useEffect(() => {
    setSelId((cur) => (String(cur ?? '') === String(preselectId ?? '') ? cur : (preselectId || null)));
  }, [preselectId]);

  // Selecciona un contrato y lo refleja en la URL (vía CarteraPage).
  const seleccionar = (id) => {
    setSelId(id);
    if (onSelectContrato) onSelectContrato(id);
  };
  const [doc, setDoc] = useState(null);        // { resumen, estado_de_cuenta, pagos, totales }
  const [liquid, setLiquid] = useState(null);   // { total, ... } AF/CS
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [incluirMora, setIncluirMora] = useState(false); // incluir moratorios en total/mensaje (default: NO cobrar)
  const listAbort = useRef(null);
  const docAbort = useRef(null);

  // ── Lista lateral (buscador) ──
  useEffect(() => {
    const ctrl = new AbortController();
    listAbort.current = ctrl;
    const t = setTimeout(async () => {
      setListLoading(true);
      try {
        const params = { per_page: 60 };
        if (query.trim()) params.search = query.trim();
        const res = await carteraApi.getCartera(params, ctrl.signal);
        if (ctrl.signal.aborted) return;
        setList(res?.data?.data || []);
      } catch (e) {
        if (!ctrl.signal.aborted && e?.code !== 'ERR_CANCELED') setList([]);
      } finally {
        if (!ctrl.signal.aborted) setListLoading(false);
      }
    }, query ? 300 : 0);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [query]);

  // ── Documento del contrato seleccionado ──
  useEffect(() => {
    setIncluirMora(false); // por defecto NO se cobra la mora al cambiar de contrato
    if (!selId) { setDoc(null); setLiquid(null); return; }
    const ctrl = new AbortController();
    docAbort.current = ctrl;
    (async () => {
      setDocLoading(true);
      setDocError(null);
      setLiquid(null);
      try {
        const res = await carteraApi.getCredito(selId, ctrl.signal);
        if (ctrl.signal.aborted) return;
        const d = res?.data?.data || null;
        setDoc(d);
        // Liquidación anticipada solo en AF/CS. El AP ya se puede liquidar
        // (oct-2026), pero su estado de cuenta (pantalla y PDF) sigue con el monto
        // estimado, igual que el que manda Conversaciones, hasta decidir mostrarla.
        if (prodCorto(d?.resumen?.tipo) !== 'AP') {
          try {
            const lq = await carteraApi.liquidacionPreview(selId, ctrl.signal);
            if (!ctrl.signal.aborted) setLiquid(lq?.data?.data || null);
          } catch (e) { /* sin datos: se omite el bloque */ }
        }
      } catch (e) {
        if (ctrl.signal.aborted || e?.code === 'ERR_CANCELED') return;
        setDocError(e?.response?.data?.message || 'No se pudo cargar el estado de cuenta.');
      } finally {
        if (!ctrl.signal.aborted) setDocLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [selId]);

  const view = useMemo(() => buildEdoCuentaView(doc, liquid), [doc, liquid]);

  const msgCtx = view ? {
    nombre: view.r.cliente, clave: view.r.clave_contrato, prod: view.prod,
    tel: view.r.tel, correo: view.r.correo, veh: view.vehiculo ? [view.vehiculo.marca, view.vehiculo.modelo].filter(Boolean).join(' ') : 'tu unidad',
    renta: view.rentaMensual, dia: view.r.dia_de_pago,
    liquidacion: view.liqTotal,
  } : {};

  // ── PDF (Descargar / Imprimir) ──────────────────────────────────────────
  const [pdfBusy, setPdfBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  // El objeto que consume EstadoCuentaPDF lo arma edoCuenta.js (compartido
  // con Conversaciones, que manda este mismo PDF por WhatsApp).
  const genPdfBlob = async () => pdf(<EstadoCuentaPDF data={buildEdoCuentaPdfData(view, incluirMora)} />).toBlob();

  // Asunto + cuerpo por defecto del correo/mensaje. NO muestra la mensualidad
  // completa: desglosa lo que REALMENTE le falta cubrir (remanente por cuota
  // vencida) + moratorios + total a regularizar.
  const buildEdoCuentaEmail = () => {
    const v = view;
    const clave = v.r.clave_contrato;
    const nombre = v.r.cliente || 'cliente';
    const { banco, clabe, contacto } = DATOS_PAGO;
    const pagoLineas = `Puedes pagar por transferencia SPEI o depósito.\nBanco: ${banco}\nCLABE: ${clabe}`;

    let cuerpo;
    if (v.hasVencido) {
      const detalle = v.vencidas
        .map((x) => `• ${x.mes}${x.parcial ? ' (resto)' : ''}: ${money(x.restante)}`)
        .join('\n');
      const conMora = incluirMora && v.mora > 0;
      const moraLn = conMora ? `\n• Moratorios: ${money(v.mora)}` : '';
      cuerpo = `Hola ${nombre},\n\n`
        + `Te compartimos tu estado de cuenta del contrato ${clave} (adjunto en PDF). Para ponerte al corriente necesitas cubrir:\n\n`
        + `${detalle}${moraLn}\n`
        + `Total a regularizar: ${money(v.vencSaldo + (conMora ? v.mora : 0))}\n\n`
        + `${pagoLineas}\n\n`
        + `Cualquier duda o aclaración estamos a tus órdenes. Contacto: ${contacto}.\n\nEquipo Peicar`;
    } else {
      cuerpo = `Hola ${nombre},\n\n`
        + `Te compartimos tu estado de cuenta del contrato ${clave} (adjunto en PDF). Tu contrato está al corriente; tu próximo pago vence el día ${v.r.dia_de_pago || '—'}.\n\n`
        + `${pagoLineas}\n\n`
        + `Gracias por tu puntualidad. — Equipo Peicar`;
    }
    return { subject: `Estado de cuenta ${clave} — PEICAR`, body: cuerpo };
  };

  const onDescargar = async () => {
    if (!view || pdfBusy) return;
    setPdfBusy(true);
    try {
      const blob = await genPdfBlob();
      saveAs(blob, `estado-cuenta-${view.r.clave_contrato}.pdf`);
    } catch (e) { /* noop */ } finally { setPdfBusy(false); }
  };

  const onImprimir = async () => {
    if (!view || pdfBusy) return;
    setPdfBusy(true);
    try {
      const blob = await genPdfBlob();
      const u = URL.createObjectURL(blob);
      const w = window.open(u, '_blank');
      // Si el visor lo permite, dispara el diálogo de impresión.
      if (w) { w.onload = () => { try { w.focus(); w.print(); } catch (e) { /* noop */ } }; }
      setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch (e) { /* noop */ } finally { setPdfBusy(false); }
  };

  return (
    <div className="co-fade" style={{ display: 'grid', gridTemplateColumns: '296px 1fr', gap: 18, alignItems: 'start' }}>
      {/* ── Lista lateral ── */}
      <div className="co-card co-no-print" style={{ overflow: 'hidden', position: 'sticky', top: 12 }}>
        <div style={{ padding: 13 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: '#fff', border: '1.5px solid #34C6F1', borderRadius: 12, padding: '9px 12px', boxShadow: '0 0 0 3px rgba(52,198,241,.08)' }}>
            <span className="pi pi-search" style={{ color: '#32B8DF', fontSize: 13 }} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar cliente o contrato…" style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, width: '100%', color: '#0F172A' }} />
          </div>
        </div>
        <div style={{ maxHeight: 640, overflowY: 'auto' }}>
          {listLoading && <div style={{ padding: '30px 16px', textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>Cargando…</div>}
          {!listLoading && list.length === 0 && <div style={{ padding: '30px 16px', textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>Sin resultados</div>}
          {!listLoading && list.map((c) => {
            const prod = prodCorto(c.tipo);
            const st = stageStyle(c.etapa_cobranza, c.estatus);
            const sel = String(selId ?? '') === String(c.id);
            const vencido = c.estatus === 'vencido' || c.estatus === 'deteriorado' || (c.dias_vencido || 0) > 0;
            return (
              <div key={c.id} onClick={() => seleccionar(c.id)} className="co-row-hover" style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 15px', borderTop: '1px solid #F4F7F9', cursor: 'pointer', background: sel ? '#EFF9FD' : '#fff', borderLeft: `3px solid ${sel ? '#32B8DF' : 'transparent'}` }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: st.bg, color: st.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{iniciales(c.cliente)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.cliente}</span>
                    {vencido && <span className="pi pi-exclamation-triangle" style={{ fontSize: 11, color: '#DC2626' }} />}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
                    <span style={{ fontSize: 9.5, fontWeight: 700, color: st.color, background: st.bg, padding: '2px 7px', borderRadius: 6 }}>{etapaLabelDe(c.etapa_cobranza, c.estatus)}</span>
                    <span className="tnum" style={{ fontSize: 11, color: '#94A3B8' }}><span style={{ color: (PROD[prod] || PROD.AP).color, fontWeight: 700 }}>{prod}</span> · {c.clave_contrato}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Panel derecho ── */}
      <div>
        {!selId && (
          <div className="co-card" style={{ padding: '96px 24px', textAlign: 'center' }}>
            <div style={{ width: 72, height: 72, borderRadius: 22, background: 'linear-gradient(135deg,#EAF6FB,#E5F8EF)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <span className="pi pi-file" style={{ fontSize: 30, color: '#32B8DF' }} />
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0F172A', marginBottom: 6 }}>Selecciona un cliente</div>
            <div style={{ fontSize: 13.5, color: '#64748B' }}>Elige un contrato de la lista para generar su estado de cuenta.</div>
          </div>
        )}

        {selId && docLoading && (
          <div className="co-card" style={{ padding: 24 }}>
            <div className="sk" style={{ height: 90, marginBottom: 16 }} />
            <div className="sk" style={{ height: 200 }} />
          </div>
        )}

        {selId && !docLoading && docError && (
          <div className="co-card" style={{ padding: '70px 24px', textAlign: 'center' }}>
            <span className="pi pi-exclamation-triangle" style={{ fontSize: 26, color: '#DC2626' }} />
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A', margin: '12px 0 6px' }}>No pudimos cargar el estado de cuenta</div>
            <div style={{ fontSize: 13, color: '#64748B' }}>{docError}</div>
          </div>
        )}

        {selId && !docLoading && !docError && view && (
          <div>
            {/* Acciones */}
            <div className="co-no-print" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
              <Pill icon={pdfBusy ? 'pi-spin pi-spinner' : 'pi-print'} disabled={pdfBusy} onClick={onImprimir}>Imprimir</Pill>
              <Pill icon={pdfBusy ? 'pi-spin pi-spinner' : 'pi-download'} disabled={pdfBusy} onClick={onDescargar}>Descargar PDF</Pill>
              <Pill icon="pi-envelope" primary onClick={() => setEmailOpen(true)}>Enviar por correo</Pill>
              <Pill icon="pi-whatsapp" waBg="#25D366" onClick={() => setMsg({ tipo: 'estado', texto: buildEdoCuentaEmail().body })}>WhatsApp</Pill>
            </div>

            {/* Documento */}
            <div className="co-card co-doc" style={{ maxWidth: 770, margin: '0 auto', overflow: 'hidden', borderRadius: 20 }}>
              <div style={{ background: '#fff', padding: '26px 28px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #EEF2F5' }}>
                <div>
                  <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', color: '#0C2036' }}>peicar</div>
                  <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 3, letterSpacing: '.02em' }}>Peicar Arrendamiento S.A.P.I. de C.V. · <span style={{ color: '#32B8DF', fontWeight: 600 }}>La llave a tu movilidad</span></div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ display: 'inline-block', fontSize: 10, fontWeight: 700, color: '#21588C', background: '#E8EFF6', textTransform: 'uppercase', letterSpacing: '.08em', padding: '4px 10px', borderRadius: 7 }}>Estado de cuenta</div>
                  <div className="tnum" style={{ fontSize: 11, color: '#64748B', marginTop: 7 }}>{fmtDate(hoyISO())}</div>
                  <div className="tnum" style={{ fontSize: 11, color: '#94A3B8' }}>Folio {view.folio}</div>
                </div>
              </div>
              <div style={{ height: 3, background: TRI }} />
              <div className="co-doc-body" style={{ padding: '24px 28px' }}>
                {/* Cliente / contrato */}
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap', paddingBottom: 18, borderBottom: '1px solid #EEF2F5' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 9 }}>
                      <span className="pi pi-user" style={{ fontSize: 12, color: '#32B8DF' }} />
                      <span style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', fontWeight: 700 }}>Cliente</span>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#0F172A' }}>{view.r.cliente}</div>
                    <div className="tnum" style={{ fontSize: 12, color: '#64748B', marginTop: 6, lineHeight: 1.7 }}>
                      {[view.r.tipo_persona === 'PM' ? 'Persona Moral' : (view.r.tipo_persona === 'PF' ? 'Persona Física' : null), view.r.rfc ? `RFC ${view.r.rfc}` : null].filter(Boolean).join(' · ') || view.r.tipo_label}
                      <br />{view.r.tel ? `Tel ${view.r.tel}` : 'Sin teléfono registrado'} · {view.r.correo || 'Sin correo'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, justifyContent: 'flex-end', marginBottom: 9 }}>
                      <span style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', fontWeight: 700 }}>Contrato</span>
                      <span className="pi pi-file" style={{ fontSize: 12, color: '#32B8DF' }} />
                    </div>
                    <div className="tnum" style={{ fontSize: 16, fontWeight: 800, color: '#21588C' }}>{view.r.clave_contrato}</div>
                    <div style={{ fontSize: 11, color: '#64748B', margin: '4px 0 8px' }}>{(PROD[view.prod] || PROD.AP).name}</div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: view.stage.color, background: view.stage.bg, padding: '3px 10px', borderRadius: 8 }}>{view.etapaLabel}</span>
                  </div>
                </div>

                {/* Vehículo */}
                {view.vehiculo && (
                  <div style={{ padding: '16px 0', borderBottom: '1px solid #EEF2F5' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 11 }}>
                      <span className="pi pi-car" style={{ fontSize: 12, color: '#32B8DF' }} />
                      <span style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', fontWeight: 700 }}>Bien arrendado</span>
                    </div>
                    <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap' }}>
                      <div><div style={{ fontSize: 10, color: '#94A3B8' }}>Vehículo</div><div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>{[view.vehiculo.marca, view.vehiculo.modelo].filter(Boolean).join(' ') || '—'}</div></div>
                      {view.vehiculo.anio && <div><div style={{ fontSize: 10, color: '#94A3B8' }}>Año</div><div className="tnum" style={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>{view.vehiculo.anio}</div></div>}
                      {view.vehiculo.placas && <div><div style={{ fontSize: 10, color: '#94A3B8' }}>Placas</div><div className="tnum" style={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>{view.vehiculo.placas}</div></div>}
                      {view.vehiculo.vin && <div><div style={{ fontSize: 10, color: '#94A3B8' }}>VIN</div><div className="tnum" style={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>{view.vehiculo.vin}</div></div>}
                    </div>
                  </div>
                )}

                {/* Detalle financiero */}
                <div style={{ padding: '18px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
                    <span className="pi pi-wallet" style={{ fontSize: 12, color: '#32B8DF' }} />
                    <span style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', fontWeight: 700 }}>Detalle financiero</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
                    {view.finCards.map((f) => <FinCard key={f.label} f={f} />)}
                  </div>
                </div>

                {/* Saldo vencido */}
                {view.hasVencido && (
                  <div style={{ background: 'linear-gradient(160deg,#FEF3F3,#FBE7E7)', border: '1px solid #F6CFCF', borderRadius: 14, padding: '15px 17px', display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ width: 36, height: 36, borderRadius: 11, background: '#FBD5D5', color: '#DC2626', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><span className="pi pi-exclamation-triangle" style={{ fontSize: 15 }} /></span>
                    <div><div style={{ fontSize: 10, color: '#B91C1C', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>Saldo vencido</div><div className="tnum" style={{ fontSize: 17, fontWeight: 800, color: '#DC2626' }}>{money(view.vencSaldo)}</div></div>
                    {view.mora > 0 && (
                      <div><div style={{ fontSize: 10, color: '#B91C1C', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>Moratorios</div><div className="tnum" style={{ fontSize: 17, fontWeight: 800, color: incluirMora ? '#DC2626' : '#B4B9C2', textDecoration: incluirMora ? 'none' : 'line-through' }}>{money(view.mora)}</div></div>
                    )}
                    <div style={{ marginLeft: 'auto', textAlign: 'right' }}><div style={{ fontSize: 10, color: '#B91C1C', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>Total a regularizar</div><div className="tnum" style={{ fontSize: 21, fontWeight: 900, color: '#DC2626' }}>{money(view.vencSaldo + (incluirMora ? view.mora : 0))}</div></div>
                  </div>
                )}
                {view.hasVencido && view.mora > 0 && (
                  <button
                    type="button"
                    onClick={() => setIncluirMora((x) => !x)}
                    className="co-no-print"
                    title="Toca para incluir o excluir los moratorios del total y del mensaje"
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,.14)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,.07)'; e.currentTarget.style.transform = 'none'; }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 9, cursor: 'pointer',
                      marginBottom: 8, padding: '9px 15px', borderRadius: 10,
                      border: incluirMora ? '1.5px solid #F1A9A9' : '1.5px solid #CBD5E1',
                      background: incluirMora ? '#FEF2F2' : '#F8FAFC',
                      color: incluirMora ? '#DC2626' : '#64748B',
                      fontSize: 13, fontWeight: 700, userSelect: 'none', fontFamily: 'inherit',
                      boxShadow: '0 1px 2px rgba(0,0,0,.07)', transition: 'box-shadow .15s, transform .15s',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <span className={'pi ' + (incluirMora ? 'pi-check-square' : 'pi-stop')} style={{ fontSize: 16 }} />
                    <span>{incluirMora ? 'Cobrando moratorios' : 'Moratorios NO incluidos'} ({money(view.mora)})</span>
                  </button>
                )}
                {view.hasVencido && (
                  <div style={{ fontSize: 10.5, color: '#94A3B8', marginBottom: 6, lineHeight: 1.5 }}>
                    Saldo vencido = lo que falta por pagar de {view.vencCount} cuota{view.vencCount === 1 ? '' : 's'} ya vencida{view.vencCount === 1 ? '' : 's'} (incluye el remanente de las que tienen abono parcial), no la suma de cuotas completas.
                  </div>
                )}

                {/* Calendario */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, margin: '18px 0 10px' }}>
                  <span className="pi pi-calendar" style={{ fontSize: 12, color: '#32B8DF' }} />
                  <span style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', fontWeight: 700 }}>Calendario de {view.isAP ? 'rentas' : 'mensualidades'}</span>
                </div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                  <div style={{ flex: 1, background: '#E5F8EF', borderRadius: 12, padding: '10px 13px' }}><div className="tnum" style={{ fontSize: 19, fontWeight: 800, color: '#00BA6E' }}>{view.pagadas}</div><div style={{ fontSize: 10, color: '#00905A', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>Pagadas</div></div>
                  <div style={{ flex: 1, background: '#EAF6FB', borderRadius: 12, padding: '10px 13px' }}><div className="tnum" style={{ fontSize: 19, fontWeight: 800, color: '#2BA7CE' }}>{view.mesActual}</div><div style={{ fontSize: 10, color: '#0E7FA6', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>Cuota actual</div></div>
                  <div style={{ flex: 1, background: '#F1F5F9', borderRadius: 12, padding: '10px 13px' }}><div className="tnum" style={{ fontSize: 19, fontWeight: 800, color: '#475569' }}>{view.faltan}</div><div style={{ fontSize: 10, color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>Por pagar</div></div>
                </div>
                <div className="co-parc-scroll" style={{ maxHeight: 340, overflow: 'auto', border: '1px solid #EEF2F5', borderRadius: 14, padding: '6px 2px' }}>
                  {view.rows.map((m, i) => {
                    const f = filaEstatus(m);
                    const isCur = i === view.curIdx;
                    // Ventana de impresión: cuota actual en medio + 4 arriba / 4 abajo.
                    const near = Math.abs(i - view.pivot) <= 4;
                    return (
                      <div key={m.numero ?? i} className={'co-parc-row' + (near ? ' co-parc-near' : '')} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: isCur ? '10px 12px' : '7px 13px', margin: isCur ? '6px 8px' : 0, border: isCur ? '1.5px solid #34C6F1' : '1px solid transparent', borderRadius: 11, background: isCur ? '#F2FBFE' : (f.key === 'vencido' ? '#FEF6F6' : 'transparent') }}>
                        <span className="tnum" style={{ width: 24, textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#94A3B8' }}>{m.numero ?? i + 1}</span>
                        <span className={'pi ' + (isCur ? 'pi-clock' : f.icon)} style={{ fontSize: 14, color: isCur ? '#2BA7CE' : f.c, width: 16, textAlign: 'center' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ fontSize: 12.5, fontWeight: 600, color: '#334155', textTransform: 'capitalize' }}>{periodoTitulo(m)}</span>
                            <span className="tnum" style={{ fontSize: 10.5, color: '#94A3B8', marginLeft: 7, flexShrink: 0 }}>{cuotaTexto(m, i, view.total)}</span>
                            {isCur &&<span style={{ fontSize: 8, fontWeight: 700, color: '#0E7FA6', background: '#DCF3FB', padding: '2px 7px', borderRadius: 5, marginLeft: 8, letterSpacing: '.05em' }}>CUOTA ACTUAL</span>}
                          </div>
                          {/* "de X · abonó Y", no "cuota X": en la misma fila ya está
                              "cuota 5/36" (el ordinal) y se leían como lo mismo. */}
                          {f.key === 'parcial' && (
                            <div className="tnum" style={{ fontSize: 10.5, color: '#94A3B8', marginTop: 2 }}>de {money0(m.pago_total)} · abonó {money0(m.monto_pagado)}</div>
                          )}
                        </div>
                        <span className="tnum" style={{ fontSize: 12.5, fontWeight: 700, color: f.key === 'pagado' ? '#00BA6E' : (f.key === 'vencido' ? '#DC2626' : (f.key === 'parcial' ? '#E5631B' : (isCur ? '#0F172A' : '#94A3B8'))) }}>{f.key === 'parcial' ? money(f.restante) : money0(m.pago_total)}</span>
                        <span style={{ fontSize: 8.5, fontWeight: 700, color: f.c, background: f.b, padding: '3px 8px', borderRadius: 6, minWidth: 70, textAlign: 'center', flexShrink: 0, whiteSpace: 'nowrap' }}>{f.label}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Liquidación */}
                <div style={{ background: 'linear-gradient(120deg,#0C2036,#21588C 68%,#2E86B0)', borderRadius: 16, padding: '18px 20px', marginTop: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap', color: '#fff' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
                    <span style={{ width: 42, height: 42, borderRadius: 13, background: 'rgba(255,255,255,.14)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><span className="pi pi-wallet" style={{ fontSize: 18 }} /></span>
                    <div>
                      <div style={{ fontSize: 11, color: '#9FC7DE', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>Liquidación anticipada {view.liqEstimada ? 'estimada' : ''}</div>
                      <div style={{ fontSize: 12, color: '#CFE3EF', marginTop: 3 }}>{view.liqDesc} · Próximo pago día {view.r.dia_de_pago || '—'}</div>
                    </div>
                  </div>
                  <div className="tnum" style={{ fontSize: 27, fontWeight: 900, letterSpacing: '-0.02em' }}>{view.liqShow}</div>
                </div>

                <div style={{ fontSize: 10.5, color: '#94A3B8', lineHeight: 1.6, marginTop: 18, paddingTop: 15, borderTop: '1px solid #EEF2F5' }}>Estado de cuenta generado el {fmtDate(hoyISO())}. Para aclaraciones: cobranza@peicar.com.mx. Montos en MXN incluyen IVA donde aplica.</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <MensajeModal open={!!msg} tipo={msg?.tipo} ctx={{ ...msgCtx, texto: msg?.texto }} onClose={() => setMsg(null)} />

      {view && emailOpen && (
        <EnviarEdoCuentaModal
          open={emailOpen}
          onClose={() => setEmailOpen(false)}
          creditoId={view.r.id}
          clave={view.r.clave_contrato}
          cliente={view.r.cliente}
          defaultTo={view.r.correo || ''}
          defaultSubject={buildEdoCuentaEmail().subject}
          defaultBody={buildEdoCuentaEmail().body}
          getPdfBlob={genPdfBlob}
        />
      )}
    </div>
  );
};

export default CarteraEstadosCuenta;
